import { addAnchorHandlers } from '../utils/anchorNav.js';

export default function tabsComponent(initialIndex = 0) {
    return {
        activeIndex: initialIndex,
        focusedIndex: null,
        totalTabs: 0,
        tabIds: [],
        panelIds: [],
        init() {
            const tabButtons = this.$refs.tabList?.querySelectorAll('[role="tab"]') || [];
            const tabPanels = this.$refs.tabPanels?.children || [];

            this.totalTabs = Math.min(tabButtons.length, tabPanels.length);

            this.tabIds = Array.from({ length: this.totalTabs }, (_, i) =>
                this.$refs.tabList?.querySelectorAll('[role="tab"]')[i]?.id || `tab-${i}`
            );

            this.panelIds = Array.from({ length: this.totalTabs }, (_, i) =>
                Array.from(tabPanels)[i]?.id || `panel-${i}`
            );

            this.activeIndex = Math.min(initialIndex, this.totalTabs - 1);

            // Check if there's a hash in the URL that matches a tab
            this.activateTabFromHash();

            // Hashchange handled centrally by anchorNav; keep initial activation for robustness.

            this.$nextTick(() => {
                this.updateAriaAttributes();
            });

            // Register anchor handlers for both tab and panel ids.
            const handlers = [];
            this.tabIds.forEach((id, i) => {
                handlers.push({ id, action: () => this.setActive(i) });
            });
            this.panelIds.forEach((id, i) => {
                // Avoid duplicate registration if panel id equals tab id
                if (!this.tabIds.includes(id)) {
                    handlers.push({ id, action: () => this.setActive(i) });
                }
            });
            if (handlers.length) {
                this._anchorCleanup = addAnchorHandlers(handlers, { updateHash: true, scroll: true });
            }
        },
        destroy() {
            if (this._anchorCleanup) {
                this._anchorCleanup();
                this._anchorCleanup = null;
            }
        },
        activateTabFromHash() {
            const hash = window.location.hash.slice(1); // Remove the # symbol
            if (!hash) return;

            // Try match against tab IDs first
            let tabIndex = this.tabIds.findIndex(id => id === hash);

            // Fallback to matching panel IDs
            if (tabIndex === -1) {
                tabIndex = this.panelIds.findIndex(id => id === hash);
            }

            if (tabIndex !== -1) {
                this.setActive(tabIndex);
                // Scroll handled by central helper; no duplicate here.
            }
        },
        isActive(index) {
            return index === this.activeIndex;
        },
        setActive(index) {
            if (index < 0 || index >= this.totalTabs) {
                return;
            }
            this.activeIndex = index;
            this.focusedIndex = index;
            this.updateAriaAttributes();
        },
        navigateTo(index) {
            if (index < 0 || index >= this.totalTabs) {
                return;
            }
            this.focusedIndex = index;
            this.updateAriaAttributes();
            this.focusTab(index);
        },
        updateAriaAttributes() {
            const tabButtons = this.$refs.tabList?.querySelectorAll('[role="tab"]') || [];
            const tabPanels = this.$refs.tabPanels?.children || [];

            const focusIndex = this.focusedIndex !== null ? this.focusedIndex : this.activeIndex;

            Array.from(tabButtons).forEach((button, index) => {
                const isActive = index === this.activeIndex;
                const isFocused = index === focusIndex;
                button.setAttribute('aria-selected', isActive);
                button.setAttribute('tabindex', isFocused ? '0' : '-1');
                button.setAttribute('aria-controls', this.panelIds[index] || '');
            });

            Array.from(tabPanels).forEach((panel, index) => {
                const isActive = index === this.activeIndex;
                panel.setAttribute('aria-hidden', !isActive);
                panel.setAttribute('aria-labelledby', this.tabIds[index] || '');
            });
        },
        focusTab(index) {
            const tabButtons = this.$refs.tabList?.querySelectorAll('[role="tab"]') || [];
            const button = Array.from(tabButtons)[index];
            if (button) {
                button.focus();
            }
        },
        handleKeydown(event, index) {
            let targetIndex = index;

            switch (event.key) {
                case 'ArrowRight':
                case 'ArrowDown':
                    event.preventDefault();
                    targetIndex = (index + 1) % this.totalTabs;
                    this.navigateTo(targetIndex);
                    break;
                case 'ArrowLeft':
                case 'ArrowUp':
                    event.preventDefault();
                    targetIndex = (index - 1 + this.totalTabs) % this.totalTabs;
                    this.navigateTo(targetIndex);
                    break;
                case 'Home':
                    event.preventDefault();
                    targetIndex = 0;
                    this.navigateTo(targetIndex);
                    break;
                case 'End':
                    event.preventDefault();
                    targetIndex = this.totalTabs - 1;
                    this.navigateTo(targetIndex);
                    break;
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    this.setActive(index);
                    break;
                default:
                    return;
            }
        }
    };
}

